// Formatting utility functions using Day.js

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export const formatters = {
  // Format date as "Jan 15, 2024"
  date: (dateString: string): string => {
    return dayjs(dateString).format('MMM D, YYYY');
  },

  // Format date and time as "Jan 15, 2024 3:30 PM"
  dateTime: (dateString: string): string => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
  },

  // Format time as "3:30 PM"
  time: (dateString: string): string => {
    return dayjs(dateString).format('h:mm A');
  },

  // Format as "2024-01-15" for input[type="date"]
  dateInput: (dateString: string): string => {
    return dayjs(dateString).format('YYYY-MM-DD');
  },

  // Format as "2024-01-15T15:30:00" for datetime-local input
  dateTimeInput: (dateString: string): string => {
    return dayjs(dateString).format('YYYY-MM-DDTHH:mm:ss');
  },

  // Relative time: "2 hours ago", "3 days ago", etc.
  relativeTime: (dateString: string): string => {
    return dayjs(dateString).fromNow();
  },

  // Relative time to now: "in 2 hours", "in 3 days", etc.
  relativeTimeTo: (dateString: string): string => {
    return dayjs(dateString).toNow();
  },

  // Check if date is today
  isToday: (dateString: string): boolean => {
    return dayjs(dateString).isSame(dayjs(), 'day');
  },

  // Check if date is in the past
  isPast: (dateString: string): boolean => {
    return dayjs(dateString).isBefore(dayjs());
  },

  // Check if date is in the future
  isFuture: (dateString: string): boolean => {
    return dayjs(dateString).isAfter(dayjs());
  },

  // Get start of day
  startOfDay: (dateString: string): string => {
    return dayjs(dateString).startOf('day').toISOString();
  },

  // Get end of day
  endOfDay: (dateString: string): string => {
    return dayjs(dateString).endOf('day').toISOString();
  },

  // Add days
  addDays: (dateString: string, days: number): string => {
    return dayjs(dateString).add(days, 'day').toISOString();
  },

  // Subtract days
  subtractDays: (dateString: string, days: number): string => {
    return dayjs(dateString).subtract(days, 'day').toISOString();
  },

  // Difference between two dates in days
  diffInDays: (date1: string, date2: string): number => {
    return dayjs(date1).diff(dayjs(date2), 'day');
  },

  // Format with custom format string
  custom: (dateString: string, format: string): string => {
    return dayjs(dateString).format(format);
  },

  // Parse and validate date
  isValid: (dateString: string): boolean => {
    return dayjs(dateString).isValid();
  },

  // Format phone number
  phoneNumber: (countryCode: string, number: string): string => {
    return `${countryCode} ${number}`;
  },

  // Format task count
  taskCount: (count: number): string => {
    return `${count} ${count === 1 ? 'task' : 'tasks'}`;
  },

  // Format file size
  fileSize: (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  },
};

// Date utility functions
export const dateUtils = {
  // Get current date/time
  now: (): string => dayjs().toISOString(),

  // Get today at start of day
  today: (): string => dayjs().startOf('day').toISOString(),

  // Get date range for last N days
  lastNDays: (days: number): { start: string; end: string } => {
    return {
      start: dayjs().subtract(days, 'day').startOf('day').toISOString(),
      end: dayjs().endOf('day').toISOString(),
    };
  },

  // Get date range for current week
  thisWeek: (): { start: string; end: string } => {
    return {
      start: dayjs().startOf('week').toISOString(),
      end: dayjs().endOf('week').toISOString(),
    };
  },

  // Get date range for current month
  thisMonth: (): { start: string; end: string } => {
    return {
      start: dayjs().startOf('month').toISOString(),
      end: dayjs().endOf('month').toISOString(),
    };
  },

  // Get date range for current year
  thisYear: (): { start: string; end: string } => {
    return {
      start: dayjs().startOf('year').toISOString(),
      end: dayjs().endOf('year').toISOString(),
    };
  },
};
